import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { LinkIcon, RefreshCwIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Spinner } from '@/components/ui/spinner';
import { useClubRoomSocket } from '@/hooks/useClubRoomSocket';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';

const COUNTERS = [
    { key: 'eligible', label: 'Elegíveis agora', hint: 'Titulares com plano e sem cobrança Asaas' },
    { key: 'pending', label: 'Na fila' },
    { key: 'active', label: 'Em execução' },
    { key: 'linked', label: 'Vinculados' },
    { key: 'notFound', label: 'Sem assinatura no Asaas' },
    { key: 'failed', label: 'Com falha' },
];

function formatDocument(value) {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return value || '—';
}

/** Super: andamento da fila de vínculo Asaas do clube, erros em tempo real e disparo da varredura. */
export default function AsaasLinkDialog({ open, onOpenChange, clubId }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(false);
    const setErrorsRef = useRef(setErrors);
    setErrorsRef.current = setErrors;

    const loadStatus = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await server.get('club/subscribers/asaas-link');
            setStatus(body?.data || null);
        } catch (error) {
            setErrorsRef.current(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) loadStatus();
    }, [open, loadStatus]);

    const { connected } = useClubRoomSocket(
        clubId ? `asaas-link:club:${clubId}` : null,
        'asaas-link:update',
        loadStatus,
        { enabled: open },
    );

    const handleStart = async () => {
        try {
            await confirm({
                title: 'Vincular Asaas',
                description: `Enfileira ${status?.eligible ?? 'os'} titular(es) com plano e sem cobrança Asaas (exceto cancelados) para procurar uma assinatura recorrente ativa no Asaas e vincular. Roda em segundo plano, até ${status?.jobsPerMinute ?? 20} por minuto.`,
                confirmationText: 'Vincular',
            });
            setStarting(true);
            const { data: body } = await server.post('club/subscribers/asaas-link');
            enqueueSnackbar(body?.message || 'Verificação enfileirada.', { variant: 'success' });
            await loadStatus();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        } finally {
            setStarting(false);
        }
    };

    const running = Boolean(status?.pending || status?.active);
    const errors = status?.errors || [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent className="w-[min(100vw-2rem,48rem)] max-w-none sm:max-w-3xl">
                <ScrollDialogHeader>
                    <DialogTitle>Vínculo com assinaturas Asaas</DialogTitle>
                    <DialogDescription>
                        Procura no Asaas a assinatura recorrente ativa de cada titular e vincula à assinatura local.
                    </DialogDescription>
                </ScrollDialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant={connected ? 'default' : 'secondary'}>
                            {connected ? 'Atualizando em tempo real' : 'Sem conexão em tempo real'}
                        </Badge>
                        {running ? (
                            <span className="flex items-center gap-1">
                                <Spinner className="size-3" />
                                Processando{status?.retrying ? ` (${status.retrying} aguardando nova tentativa)` : ''}
                            </span>
                        ) : null}
                    </div>

                    {!status && loading ? (
                        <div className="flex items-center justify-center gap-2 py-10">
                            <Spinner className="size-5" />
                            <span className="text-sm text-muted-foreground">Carregando…</span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {COUNTERS.map(counter => (
                                <div key={counter.key} className="rounded-lg border px-3 py-2" title={counter.hint}>
                                    <p className="text-xs text-muted-foreground">{counter.label}</p>
                                    <p className="text-lg font-semibold tabular-nums">{status?.[counter.key] ?? 0}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                        Vinculados, sem assinatura e falhas contam as execuções recentes guardadas pela fila.
                    </p>

                    <div className="flex flex-col gap-2">
                        <h3 className="text-sm font-medium">Erros ({status?.errorsTotal ?? 0})</h3>
                        {errors.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Nenhum assinante com erro de vínculo.</p>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                                        <tr>
                                            <th className="px-3 py-2 font-medium">Assinante</th>
                                            <th className="px-3 py-2 font-medium">Erro</th>
                                            <th className="px-3 py-2 font-medium">Quando</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {errors.map(row => (
                                            <tr key={row.subscriptionId} className="border-t align-top">
                                                <td className="px-3 py-2">
                                                    <p className="font-medium">{row.name || '—'}</p>
                                                    <p className="text-xs tabular-nums text-muted-foreground">
                                                        {formatDocument(row.document)}
                                                    </p>
                                                </td>
                                                <td className="px-3 py-2 text-destructive">{row.error}</td>
                                                <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                                                    {row.updatedAt ? format(new Date(row.updatedAt), 'dd/MM/yyyy HH:mm') : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {errors.length > 0 ? (
                            <Alert>
                                <AlertTitle>Como resolver</AlertTitle>
                                <AlertDescription>
                                    Abra o assinante e use a aba Asaas para escolher a assinatura certa. Vincular limpa o erro.
                                </AlertDescription>
                            </Alert>
                        ) : null}
                    </div>
                </DialogBody>

                <ScrollDialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        Fechar
                    </Button>
                    <Button type="button" variant="outline" disabled={loading} onClick={loadStatus}>
                        {loading ? <Spinner className="size-4" /> : <RefreshCwIcon className="size-4" />}
                        Atualizar
                    </Button>
                    <Button type="button" disabled={starting || !status?.eligible} onClick={handleStart}>
                        {starting ? <Spinner className="size-4" /> : <LinkIcon className="size-4" />}
                        Vincular Asaas
                    </Button>
                </ScrollDialogFooter>
            </ScrollDialogContent>
        </Dialog>
    );
}
